import { Button } from "@gigpro/ui/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@gigpro/ui/Collapsible";
import { createColumnHelper, DataTable } from "@gigpro/ui/DataTable";
import { ChevronDown } from "@gigpro/ui/Icons";
import { cn } from "@gigpro/ui/tailwind/cn";
import { formatDateTime } from "@gigpro/utils/datetime";
import { humanize } from "domains/admin/humanize";
import React from "react";
import {
  type ShiftBreakHistoryRevision,
  type ShiftBreakHistoryValue,
  useShiftBreakHistory,
} from "./useShiftBreakHistory";

type HistoryRow = {
  id: number;
  user: string;
  field: string;
  old_value: ShiftBreakHistoryValue;
  new_value: ShiftBreakHistoryValue;
  timestamp: Date;
};

function formatHistoryValue(value: ShiftBreakHistoryValue): string {
  if (value == null) return "—";
  if (value instanceof Date) return formatDateTime(value, "MM/dd/yyyy", null);
  return String(value);
}

function toHistoryRows(revisions: Array<ShiftBreakHistoryRevision> | undefined): Array<HistoryRow> {
  let id = 0;
  return (revisions ?? []).flatMap((revision) =>
    revision.changes.map((change) => ({
      id: id++,
      user: revision.user,
      field: change.field,
      old_value: change.old_value,
      new_value: change.new_value,
      timestamp: revision.timestamp,
    })),
  );
}

const historyColumnHelper = createColumnHelper<HistoryRow>();
const historyColumns = [
  historyColumnHelper.accessor("user", { header: "Changed By", enableColumnFilter: false, enableSorting: false }),
  historyColumnHelper.accessor("field", {
    header: "Field",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue }) => humanize(getValue()),
  }),
  historyColumnHelper.display({
    header: "From → To",
    cell: ({ row }) => `${formatHistoryValue(row.original.old_value)} → ${formatHistoryValue(row.original.new_value)}`,
  }),
  historyColumnHelper.accessor("timestamp", {
    header: "When",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue }) => formatDateTime(getValue(), "MM/dd/yyyy", null),
  }),
];

interface BreakChangeHistoryProps {
  breakId: string;
  label?: string;
  className?: string;
}

export function BreakChangeHistory({
  breakId,
  label = "Change History",
  className,
}: Readonly<BreakChangeHistoryProps>) {
  const [open, setOpen] = React.useState(false);
  const history = useShiftBreakHistory(open ? breakId : undefined);
  const rows = toHistoryRows(history.data);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className={cn("w-full", className)}>
      <CollapsibleTrigger asChild>
        <Button
          variant="outlined"
          className="w-full data-[state=open]:rounded-b-none data-[state=open]:[&>svg]:rotate-180"
          loading={history.isLoading}
        >
          {label}
          <ChevronDown className="ml-auto transition-transform" />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {history.isError ? (
          <p className="px-2 py-1 text-small text-error">Unable to load change history.</p>
        ) : (
          <DataTable hideToolbar columns={historyColumns} data={rows} loading={history.isLoading} />
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
